import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { CURRENCIES_DICTIONARY_QUERY, LINK_ITEMS, TAX_RATES } from 'features/common/queries';
import { LEASING_EXPENSE_TYPE_QUERY } from '../../../queries';
import { useQuery, useClientQuery, useMutation, useSubscription } from 'hooks/apollo';
import { expenseNameVar } from '../../variables';
import { expenseTypeValidator } from '../../validators';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { usePermission } from 'providers/RightsHooks';
import LeasingExpenseTypeComponent from './LeasingExpenseTypeComponent';
import { SAVE_LEASING_EXPENSE_TYPE } from 'features/leasingExpenseType/mutations';
import { LEASING_EXPENSE_TYPE_SAVED } from 'features/leasingExpenseType/subscriptions';
import { ADMIN_LEASING_EXPENSE_TYPES_WRITE } from 'constants/rightsCodes';

const LeasingExpenseTypeContainer = ({ expenseTypeId, onRedirect }) => {
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();
  const [pending, setPending] = useState(false);
  const [validation, validate] = useDirtyFieldValidation(expenseTypeValidator);
  const addToast = useToast();
  const { t } = useTranslation();
  const clientQuery = useClientQuery();
  const canWrite = usePermission(ADMIN_LEASING_EXPENSE_TYPES_WRITE);

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);
  const disabled = useMemo(() => !canWrite || pending, [canWrite, pending]);

  const { data: currenciesDictionaryData } = useQuery(CURRENCIES_DICTIONARY_QUERY);

  const { data: taxRatesData } = useQuery(TAX_RATES);

  useQuery(LEASING_EXPENSE_TYPE_QUERY, {
    skip: !expenseTypeId,
    variables: { expenseTypeId },
    onCompleted: data => {
      resetLens(data.leasingExpenseType);
    }
  });

  const linkItem = { id: lens.linkedItemId |> get, name: lens.linkedItemName |> get };

  const loadLinkItems = useCallback(
    async search => {
      const { data } = await clientQuery(LINK_ITEMS, {
        variables: { search }
      });
      return data?.linkItems || [];
    },
    [clientQuery]
  );

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  useEffect(() => {
    const name = get(lens.expenseName) ?? '-';
    expenseNameVar(name);
  }, [lens]);

  // #region Save

  const [saveLeasingExpenseType] = useMutation(SAVE_LEASING_EXPENSE_TYPE, {
    onError: () => {
      setPending(false);
    }
  });

  const handleSave = useCallback(() => {
    const data = lens |> get;
    if (!validate(data)) return;
    // eslint-disable-next-line no-unused-vars
    const { linkedItemName, currencyCode, vatName, ...sanitizedData } = data;
    setPending(true);
    saveLeasingExpenseType({
      variables: { input: sanitizedData }
    });
  }, [lens, saveLeasingExpenseType, validate]);

  useSubscription(LEASING_EXPENSE_TYPE_SAVED, {
    onData: ({
      data: {
        data: { leasingExpenseTypeSaved }
      },
      client
    }) => {
      resetLens(leasingExpenseTypeSaved.leasingExpenseType);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingExpenseType' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingExpenseTypes' });
      client.cache.gc();
      if (!expenseTypeId) {
        const newId = leasingExpenseTypeSaved.leasingExpenseType.expenseTypeId;
        onRedirect(newId);
      }
      setPending(false);
      addToast(t('LeasingExpenseType.Saved'), 'success');
    }
  });

  // #endregion

  return (
    <>
      <LeasingExpenseTypeComponent
        lens={lens}
        validation={validation}
        currencies={currenciesDictionaryData?.currenciesDictionary}
        linkItem={linkItem}
        loadLinkItems={loadLinkItems}
        taxRates={taxRatesData?.taxRateItems}
        isDirty={isDirty}
        disabled={disabled}
        onSave={handleSave}
      />
    </>
  );
};

LeasingExpenseTypeContainer.propTypes = {
  expenseTypeId: PropTypes.number.isRequired,
  onRedirect: PropTypes.func
};

export default LeasingExpenseTypeContainer;
